import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import texturePath from '../../assets/img/textures/decal.png';
import CustomShaderMaterial from './CustomShaderMaterial';
import modelPath from '../../assets/models/tiny-man.glb';
import { coloursToArray } from '../../utils';
import { MeshLambertMaterial } from 'three';

export default class SceneController {
    constructor(domElem) {
        this.domElem = domElem;
        this.scene = new THREE.Scene();
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
    }

    setup() {
        this.colourArr = (0, coloursToArray)('https://coolors.co/fb6107-f3de2c-7cb518-5c8001-fbb02d');
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.domElem.appendChild(this.renderer.domElement);

        this.disposed = false;
        this.scene.background = this.colourArr[0];
        this.scene.fog = new THREE.Fog(this.colourArr[0], 2, 200);

        this.setupCamera();
        this.setupLight();
        this.setupControls();
        this.loadModels();
        this.setupSceneSubjects();
    };

    setupCamera() {
        let VIEW_ANGLE = 70;
        let ASPECT_RATIO = window.innerWidth / window.innerHeight;
        let NEAR_PLANE = 0.1;
        let FAR_PLANE = 100;

        this.camera = new THREE.PerspectiveCamera(VIEW_ANGLE, ASPECT_RATIO, NEAR_PLANE, FAR_PLANE);
        this.camera.position.set(0, 2, 4);
    };

    setupLight() {
        let pointLight = new THREE.PointLight(this.colourArr[3], 2.);
        pointLight.position.set(1.5, 1.5, 2.5);
        this.scene.add(pointLight);

        let pointLight2 = new THREE.PointLight(this.colourArr[0], 1.);
        pointLight2.position.set(-1.5, 0, -2.5);
        this.scene.add(pointLight2);

        let pointLight3 = new THREE.PointLight(this.colourArr[1], 1.);
        pointLight3.position.set(0.5, -1, 0);
        this.scene.add(pointLight3);

        let hemiLight = new THREE.HemisphereLight(this.colourArr[4], this.colourArr[3], 1);
        hemiLight.position.set(0, 0.5, 0);
        this.scene.add(hemiLight);

        let dirLight = new THREE.DirectionalLight(this.colourArr[1], 1);
        this.scene.add(dirLight);
    };

    setupControls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.autoRotate = true;
        this.controls.autoRotateSpeed = 0.5;
        this.controls.zoomSpeed = 1.2;
        this.controls.panSpeed = 0.8;
        this.controls.enableZoom = true;
        this.controls.enablePan = true;
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.5;
        this.controls.maxDistance = 10;
        this.controls.minDistance = 1;
    };

    loadModels() {
        let gltfLoader = new GLTFLoader();
        gltfLoader.load(modelPath, (gltf) => {
            gltf.scene.scale.set(0.2, 0.2, 0.2);
            gltf.scene.position.set(0, -0.3, 0);
            gltf.scene.traverse(child => {
                if (child.isMesh) {
                    child.material.metalness = 1;

                }
            });
            this.scene.add(gltf.scene);
            this.model = gltf.scene;
        });
        console.log('Loading Complete!');
    };

    loadTextures(path) {
        let textureLoader = new THREE.TextureLoader();
        let texture = textureLoader.load(path, (texture) => {
            return texture;
        });
        return texture;
    };

    makeSphere() {
        const radius = 2;
        const numElems = 30;
        const geo = new THREE.SphereBufferGeometry(0.2, 32, 32);
        const material = new MeshLambertMaterial({ color: this.colourArr[1] });
        for (let i = 0; i < numElems; i++) {
            const mesh = new THREE.Mesh(geo, material);
            const phi = Math.acos(- 1 + (2 * i) / numElems);
            const theta = Math.sqrt(numElems * Math.PI) * phi;
            mesh.position.setFromSphericalCoords(radius, phi, theta);
            this.scene.add(mesh);
        }
    }

    setupSceneSubjects() {
        const geo = new THREE.IcosahedronBufferGeometry(1);
        const edgeGeo = new THREE.EdgesGeometry(geo);
        const texture = this.loadTextures(texturePath);
        this.material = new CustomShaderMaterial(this.colourArr[1], texture);

        const mesh = new THREE.Mesh(geo, this.material.makeMaterial());
        const edgeLines = new THREE.LineSegments(edgeGeo, this.material.makeLineMaterial());
        this.scene.add(mesh);
        this.scene.add(edgeLines);
        this.makeSphere();
    };

    update() {
        if (this.disposed) return;
        if (this.controls) this.controls.update();
        if (this.material) this.material.update();
        this.renderer.render(this.scene, this.camera);
    };

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    };

    dispose() {
        this.disposed = true;
        this.scene.children.forEach(child => {
            if (child.isMesh) {
                // child.material.dispose(); //TODO: fix mistake with this line
                child.geometry.dispose();
            }
        });
        this.scene.dispose();
        this.renderer.dispose();
        this.domElem.removeChild(this.renderer.domElement);
    };
}

